import z from 'zod/v4';
import { ResolutionSchema } from './miscSchemas.js';
import { ProgramSchema } from './programmingSchema.js';
import { SubtitlePreference } from './subtitleSchema.js';
import { ChannelIconSchema, ContentProgramTypeSchema, StrictChannelIconSchema, } from './utilSchemas.js';
export const WatermarkSchema = z.object({
    url: z.string().optional(),
    enabled: z.boolean(),
    position: z
        .union([
        z.literal('top-left'),
        z.literal('top-right'),
        z.literal('bottom-left'),
        z.literal('bottom-right'),
    ])
        .default('bottom-right'),
    width: z.number().positive(),
    verticalMargin: z.number().min(0).max(100),
    horizontalMargin: z.number().min(0).max(100),
    duration: z.number().min(0).default(0),
    fixedSize: z.boolean().optional(),
    animated: z.boolean().optional(),
    opacity: z.number().min(0).max(100).int().optional().catch(100).default(100),
    fadeConfig: z
        .array(z.object({
        programType: ContentProgramTypeSchema.optional().catch(undefined),
        // Encodes on/off period of displaying the watermark in mins.
        // e.g. a 5m period fades in the watermark every 5th min and displays it
        // for 5 mins.
        periodMins: z.number().positive().min(1),
        // If true, the watermark will fade in immediately on channel stream start.
        // If false, the watermark will start not visible and fade in after periodMins.
        leadingEdge: z.boolean().optional().catch(true),
    }))
        .optional(),
});
/**
 * The watermark as accepted from a client.
 *
 * Same split as StrictChannelIconSchema: identical handling of a *missing*
 * field, validation failure instead of silent substitution for an *invalid*
 * one. The `.catch()` calls in WatermarkSchema above are kept because that
 * schema also sits on channel responses, which are serialized from an
 * unvalidated JSON column.
 *
 * What they were hiding:
 * - `opacity: 150`, `-10`, `50.5` or `"50"` was stored as 100, fully opaque.
 * - `fadeConfig[].programType: "movies"` (not a ContentProgramType) became
 *   undefined, which means "no restriction" — so a fade rule the user scoped
 *   to one program type silently applied to every program on the channel.
 * - `fadeConfig[].leadingEdge: "false"`, as a form serialiser would send it,
 *   became true, the opposite of what was asked for.
 */
export const StrictWatermarkSchema = WatermarkSchema.extend({
    opacity: z.number().min(0).max(100).int().optional().default(100),
    fadeConfig: z
        .array(z.object({
        programType: ContentProgramTypeSchema.optional(),
        periodMins: z.number().positive().min(1),
        leadingEdge: z.boolean().optional(),
    }))
        .optional(),
});
export const FillerCollectionSchema = z.object({
    id: z.string(),
    weight: z.number(),
    cooldownSeconds: z.number(),
});
export const ChannelOfflineSchema = z.object({
    picture: z.string().optional(),
    soundtrack: z.string().optional(),
    mode: z.union([z.literal('pic'), z.literal('clip')]),
});
export const ChannelTranscodingOptionsSchema = z.object({
    targetResolution: ResolutionSchema.optional(),
    videoBitrate: z.number().optional(),
    videoBufferSize: z.number().optional(),
});
export const HlsChannelStreamMode = 'hls';
export const HlsConcatChannelStreamMode = 'hls_concat';
export const HlsSlowerChannelStreamMode = 'hls_slower';
export const HlsSlowerConcatChannelStreamMode = 'hls_slower_concat';
export const MpegTsChannelStreamMode = 'mpegts';
export const MpegTsConcatChannelStreamMode = 'mpegts_concat';
export const HlsDirectStreamMode = 'hls_direct';
export const HlsDirectConcatStreamMode = 'hls_direct_concat';
export const HlsDirectV2StreamMode = 'hls_direct_v2';
export const HlsDirectV2ConcatStreamMode = 'hls_direct_v2_concat';
export const EtvNextStreamMode = 'etv_next';
export const EtvNextConcatStreamMode = 'etv_next_concat';
export const ChannelStreamMode = {
    Hls: HlsChannelStreamMode,
    HlsSlower: HlsSlowerChannelStreamMode,
    MpegTs: MpegTsChannelStreamMode,
    HlsDirect: HlsDirectStreamMode,
    HlsDirectV2: HlsDirectV2StreamMode,
};
export const ChannelConcatStreamMode = {
    Hls: HlsConcatChannelStreamMode,
    HlsSlower: HlsSlowerConcatChannelStreamMode,
    MpegTs: MpegTsConcatChannelStreamMode,
    HlsDirect: HlsDirectConcatStreamMode,
    HlsDirectV2: HlsDirectV2ConcatStreamMode,
};
export const ChannelStreamModes = [
    ChannelStreamMode.Hls,
    ChannelStreamMode.HlsSlower,
    ChannelStreamMode.MpegTs,
    ChannelStreamMode.HlsDirect,
    ChannelStreamMode.HlsDirectV2,
];
export const ChannelConcatStreamModes = [
    ChannelConcatStreamMode.Hls,
    ChannelConcatStreamMode.HlsSlower,
    ChannelConcatStreamMode.MpegTs,
    ChannelConcatStreamMode.HlsDirect,
    ChannelConcatStreamMode.HlsDirectV2,
];
export const ChannelStreamModeSchema = z.enum(ChannelStreamModes);
export const ChannelConcatStreamModeSchema = z.enum(ChannelConcatStreamModes);
/**
 * Stream modes a running session can have, as opposed to the modes a channel
 * can be saved as. `etv_next` is a backend the feature flag routes to, not a
 * setting, so it appears in session keys and stream URLs but never on a
 * channel or in the database.
 */
export const SessionStreamModes = [
    ...ChannelStreamModes,
    EtvNextStreamMode,
];
export const SessionConcatStreamModes = [
    ...ChannelConcatStreamModes,
    EtvNextConcatStreamMode,
];
export const SessionStreamModeSchema = z.enum(SessionStreamModes);
export const SessionConcatStreamModeSchema = z.enum(SessionConcatStreamModes);
export const StreamConnectionDetailsSchema = z.object({
    ip: z.ipv4().or(z.ipv6()),
    userAgent: z.string().optional(),
    lastHeartbeat: z.number().nonnegative().optional(),
    lastHeartbeatStr: z.iso.datetime({ offset: true }).optional(),
});
export const ChannelSessionSchema = z.object({
    type: z.enum([...SessionStreamModes, ...SessionConcatStreamModes]),
    state: z.string(),
    numConnections: z.number().nonnegative(),
    connections: z.array(StreamConnectionDetailsSchema),
});
export const ChannelSchema = z.object({
    disableFillerOverlay: z.boolean(),
    duration: z.number(),
    fallback: z.array(ProgramSchema).optional(),
    fillerCollections: z.array(FillerCollectionSchema).optional(),
    fillerRepeatCooldown: z.number().optional(),
    groupTitle: z.string(),
    guideFlexTitle: z.string().optional(),
    guideMinimumDuration: z.number(),
    icon: ChannelIconSchema,
    id: z.string(),
    name: z.string(),
    number: z.number(),
    offline: ChannelOfflineSchema,
    startTime: z.number(),
    stealth: z.boolean(),
    transcoding: ChannelTranscodingOptionsSchema.optional(),
    watermark: WatermarkSchema.optional(),
    onDemand: z.object({
        enabled: z.boolean(),
    }),
    programCount: z.number(),
    streamMode: ChannelStreamModeSchema,
    transcodeConfigId: z.uuid(),
    sessions: z.array(ChannelSessionSchema).optional(),
    subtitlesEnabled: z.boolean(),
    /**
     * Routes this channel to the ErsatzTV next backend while the global feature
     * flag is off. The flag enrolls every channel on its own, so this only
     * decides anything when the flag is off. That is what lets a user move
     * channels over one at a time before flipping the flag for the rest.
     *
     * Defaulted rather than required so a client written before the field
     * existed can still save a channel.
     */
    useEtvNext: z.boolean().default(false),
    subtitlePreferences: z.array(SubtitlePreference).nonempty().optional(),
});
// The write path validates strictly: a bad icon or watermark value is a 400,
// not a 200 that stored something else. The read path (ChannelSchema, used on
// channel responses) stays lenient so existing rows still serialize.
export const SaveableChannelSchema = ChannelSchema.omit({
    fallback: true, // Figure out how to update this
    programCount: true,
    transcoding: true,
    sessions: true,
})
    .partial({
    onDemand: true,
})
    .extend({
    icon: StrictChannelIconSchema,
    watermark: StrictWatermarkSchema.optional(),
});
// A missing startTime leaves the stored value unchanged. Server-computed
// rebases are exact and must survive saves of unrelated settings.
export const UpdateChannelRequestSchema = SaveableChannelSchema.partial({
    startTime: true,
});
export const NewChannelSaveRequestSchema = z.object({
    type: z.literal('new'),
    channel: SaveableChannelSchema,
});
export const CopyChannelSaveRequestSchema = z.object({
    type: z.literal('copy'),
    channelId: z.string().uuid(),
});
export const CreateChannelRequestSchema = z.discriminatedUnion('type', [
    NewChannelSaveRequestSchema,
    CopyChannelSaveRequestSchema,
]);
//# sourceMappingURL=channelSchema.js.map